<template>
  <div class="padding">
    <!-- Page header -->
    <div class="fg-portal-head">
      <div>
        <h5 class="_300 m-b-0">User Guideline</h5>
      </div>
      <a
        :href="viewAllUrl"
        class="btn primary"
      >
        View All
      </a>
    </div>

    <!-- Guidelines section -->
    <div class="box">
      <div class="box-body">
        <div class="fg-portal-toolbar">
          <multiselect
            v-model="selectedCategory"
            :options="categories"
            :show-labels="false"
            class="fg-portal-select"
            label="name"
            track-by="id"
            placeholder="All categories"
            @input="fetchGuidelines"
          />
          <div class="fg-portal-search-wrap">
            <input
              v-model="guidelineQuery"
              :class="{ 'is-invalid': guidelineQueryError }"
              class="form-control fg-portal-search"
              type="text"
              placeholder="Search"
              @input="debouncedGuidelines"
            >
            <small
              v-if="guidelineQueryError"
              class="text-danger"
            >{{ guidelineQueryError }}</small>
          </div>
        </div>

        <div
          v-if="loadingGuidelines"
          class="text-center text-muted p-y-2"
        >
          Loading...
        </div>
        <div
          v-else-if="!guidelines.length"
          class="text-center text-muted p-y-2"
        >
          No guidelines found.
        </div>
        <div
          v-else
          class="fg-portal-grid"
        >
          <faq-guideline-portal-card
            v-for="item in guidelines"
            :key="item.id"
            :item="item"
            @detail="openDetail"
          />
        </div>
      </div>
    </div>

    <!-- FAQ section -->
    <h6 class="_300 m-t-lg m-b-md">Frequently Asked Questions (FAQ)</h6>
    <div class="box">
      <div class="box-body">
        <div class="fg-portal-toolbar">
          <multiselect
            v-model="selectedFaq"
            :options="faqOptions"
            :show-labels="false"
            :allow-empty="false"
            class="fg-portal-select"
            label="title"
            track-by="id"
            placeholder="Select FAQ"
            @input="loadFaqDetail"
          />
          <input
            v-model="faqQuery"
            class="form-control fg-portal-search"
            type="text"
            placeholder="Search"
          >
        </div>

        <div
          v-if="!faqOptions.length"
          class="text-center text-muted p-y-2"
        >
          No FAQ available.
        </div>
        <div
          v-else
          id="fgPortalFaqAccordion"
          class="accordion"
        >
          <div
            v-for="(faq, index) in filteredFaqContents"
            :key="faq.id"
            class="card"
          >
            <div
              :id="'fgPortalHeading' + index"
              :data-target="'#fgPortalCollapse' + index"
              :aria-controls="'fgPortalCollapse' + index"
              class="card-header fg-faq-header"
              data-toggle="collapse"
              aria-expanded="false"
            >
              <strong>{{ faq.question }}</strong>
            </div>
            <div
              :id="'fgPortalCollapse' + index"
              :aria-labelledby="'fgPortalHeading' + index"
              class="collapse"
              data-parent="#fgPortalFaqAccordion"
            >
              <div class="card-body">
                {{ faq.answer }}
              </div>
            </div>
          </div>
          <div
            v-if="!filteredFaqContents.length"
            class="text-center text-muted p-y-2"
          >
            No questions match your search.
          </div>
        </div>
      </div>
    </div>

    <faq-guideline-detail
      ref="detail"
      :show-acknowledge="true"
      @acknowledged="onAcknowledged"
    />
  </div>
</template>

<script>
import FaqGuidelinePortalCard from './faq-guideline-portal-card.vue';
import FaqGuidelineDetail from './faq-guideline-detail.vue';

const Laroute = require('../../laroute');

export default {
    components: {
        FaqGuidelinePortalCard,
        FaqGuidelineDetail,
    },

    data() {
        return {
            categories: [],
            selectedCategory: null,
            guidelineQuery: '',
            guidelines: [],
            loadingGuidelines: false,
            faqOptions: [],
            selectedFaq: null,
            currentFaq: {},
            faqQuery: '',
            debounceTimer: null,
            viewAllUrl: Laroute.route('franchise.faq-guideline.view-all'),
        };
    },

    computed: {
        guidelineQueryError() {
            return this.htmlTagsSearchError(this.guidelineQuery);
        },

        filteredFaqContents() {
            const contents = this.currentFaq.faq_contents || [];
            const q = this.faqQuery.trim().toLowerCase();

            if (!q) {
                return contents;
            }

            return contents.filter(c =>
                (c.question || '').toLowerCase().includes(q),
            );
        },
    },

    created() {
        this.loadCategories();
        this.fetchGuidelines();
        this.loadFaqOptions();
    },

    methods: {
        loadCategories() {
            this.$store
                .dispatch('faqGuideline/getFaqGuidelineCategories')
                .then(categories => {
                    this.categories = categories;
                });
        },

        fetchGuidelines() {
            // Don't fire a request the server will reject — a 422 here would
            // land in the catch below and show a generic error popup.
            if (this.guidelineQueryError) {
                return;
            }

            const params = {
                type: 'guideline',
                category_id: this.selectedCategory
                    ? this.selectedCategory.id
                    : undefined,
                query: this.guidelineQuery || undefined,
                page: 1,
                limit: 3,
            };

            this.loadingGuidelines = true;
            this.$store
                .dispatch('faqGuideline/getFaqGuidelines', params)
                .then(res => {
                    this.guidelines = res.data.data;
                })
                .catch(() => {
                    this.swalError({ text: 'Unable to load guidelines.' });
                })
                .finally(() => {
                    this.loadingGuidelines = false;
                });
        },

        debouncedGuidelines() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => this.fetchGuidelines(), 350);
        },

        loadFaqOptions() {
            this.$store
                .dispatch('faqGuideline/getFaqGuidelines', {
                    type: 'faq',
                    limit: 0,
                })
                .then(res => {
                    this.faqOptions = res.data.data;
                    if (this.faqOptions.length) {
                        this.selectedFaq = this.faqOptions[0];
                        this.loadFaqDetail(this.selectedFaq);
                    }
                });
        },

        loadFaqDetail(faq) {
            if (!faq) {
                this.currentFaq = {};
                return;
            }

            this.$store
                .dispatch('faqGuideline/getFaqGuideline', faq.id)
                .then(guideline => {
                    this.currentFaq = guideline;
                });
        },

        openDetail(item) {
            this.$store
                .dispatch('faqGuideline/getFaqGuideline', item.id)
                .then(guideline => {
                    this.$refs.detail.show(guideline);
                });
        },

        /**
         * Patch the card in place from the acknowledge response, then refetch.
         * The refetch alone left the "Unread" pill stale until a page reload
         * (PS-8898); updating the item we already hold makes the pill correct
         * the moment the acknowledgement lands.
         */
        onAcknowledged(guideline) {
            this.patchGuideline(guideline);
            this.fetchGuidelines();
        },

        patchGuideline(guideline) {
            if (!guideline) {
                return;
            }

            const index = this.guidelines.findIndex(g => g.id === guideline.id);

            if (index !== -1) {
                this.guidelines.splice(index, 1, guideline);
            }
        },
    },
};
</script>

<style scoped>
.fg-portal-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;
}

.fg-portal-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 18px;
}
.fg-portal-select {
    width: 240px;
    flex: 0 0 auto;
}
/* The wrapper carries the layout (it holds the input plus its validation
   message); the input just fills it. */
.fg-portal-search-wrap {
    width: 260px;
    max-width: 50%;
    margin-left: auto;
    flex: 0 0 auto;
}
.fg-portal-search {
    width: 100%;
}

.fg-portal-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

@media (max-width: 991.98px) {
    .fg-portal-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}
@media (max-width: 575.98px) {
    .fg-portal-grid {
        grid-template-columns: 1fr;
    }
    .fg-portal-toolbar {
        flex-wrap: wrap;
    }
    .fg-portal-select,
    .fg-portal-search-wrap {
        width: 100%;
        max-width: 100%;
        margin-left: 0;
    }
}
</style>

<style scoped>
/* FAQ accordion — mirrors faq-guideline-detail.vue styles. */
#fgPortalFaqAccordion .card {
    margin-bottom: 10px;
    border: 1px solid #e9edf0;
    border-radius: 8px;
    overflow: hidden;
}
#fgPortalFaqAccordion .card-header {
    background: #f6f8f9;
    border-bottom: 0;
}
.fg-faq-header {
    cursor: pointer;
    padding: 14px 18px;
}
#fgPortalFaqAccordion .card-body {
    padding: 14px 18px;
    font-size: 14px;
    line-height: 1.6;
    color: #647285;
    border-top: 1px solid #eef1f3;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/faq_guideline/faq-guideline-portal.vue